import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { hasTier } from "@/lib/tiers";
import { PaywallGate } from "@/components/PaywallGate";
import { BookOpen, Download, Loader2, Compass, ExternalLink, List } from "lucide-react";

const COPY = {
  en: {
    kicker: "DIY Legal · Court Ready +",
    heading: "Court Ready — the book",
    sub: "Read the full guide in your browser or download the PDF for offline use. Court Ready+ members only.",
    download: "Download PDF",
    open_new: "Open in a new tab",
    loading: "Preparing the book...",
    chapters: "Chapters",
    empty_chapters: "No chapters detected — use scroll or search inside the PDF.",
    jump: (n) => `Page ${n}`,
    disclaimer: "This book is educational and procedural information about Illinois civil court. It is not legal advice, does not create an attorney-client relationship, and does not replace consultation with a licensed attorney.",
    author_line: "by Nina Watts",
  },
  es: {
    kicker: "DIY Legal · Court Ready +",
    heading: "Court Ready — el libro",
    sub: "Lee la guía completa en tu navegador o descarga el PDF para uso sin conexión. Solo para miembros Court Ready+.",
    download: "Descargar PDF",
    open_new: "Abrir en una pestaña nueva",
    loading: "Preparando el libro...",
    chapters: "Capítulos",
    empty_chapters: "No se detectaron capítulos — usa desplazamiento o búsqueda dentro del PDF.",
    jump: (n) => `Página ${n}`,
    disclaimer: "Este libro es información educativa y procesal sobre la corte civil de Illinois. No es asesoría legal, no crea una relación abogado-cliente y no reemplaza la consulta con un abogado con licencia.",
    author_line: "por Nina Watts",
  },
};

function ReaderApp() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState("");
  const [page, setPage] = useState(1);
  const iframeRef = useRef(null);
  const activeBtnRef = useRef(null);
  const backend = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    api.get("/court-ready/meta")
      .then((r) => setMeta(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Please try again."));
  }, []);

  const pdfHref = `${backend}/api/court-ready/pdf`;
  const dlHref = `${backend}/api/court-ready/pdf?download=1`;
  const iframeSrc = `${pdfHref}#page=${page}&zoom=page-fit`;

  const jumpTo = (p) => {
    setPage(p);
    // Force iframe reload to honor the new #page anchor across browsers.
    if (iframeRef.current) {
      iframeRef.current.src = `${pdfHref}#page=${p}&zoom=page-fit`;
    }
  };

  // Chapter Sync Highlight: poll the iframe's URL hash for page changes
  // triggered by native PDF viewer navigation, then compute the current
  // chapter (greatest chapter.page <= current page) and highlight it.
  useEffect(() => {
    if (!meta?.chapters?.length) return;
    const id = setInterval(() => {
      try {
        const win = iframeRef.current && iframeRef.current.contentWindow;
        const hash = win && win.location && win.location.hash;
        if (!hash) return;
        const m = hash.match(/page=(\d+)/);
        if (!m) return;
        const p = parseInt(m[1], 10);
        if (!Number.isFinite(p) || p === page) return;
        setPage(p);
      } catch {
        // Cross-origin or nav in progress — ignore silently.
      }
    }, 1500);
    return () => clearInterval(id);
  }, [meta, page]);

  // Determine which chapter is "current" based on page state.
  const currentChapterPage = React.useMemo(() => {
    const chs = meta?.chapters || [];
    if (!chs.length) return null;
    let match = chs[0].page;
    for (const ch of chs) {
      if (ch.page <= page) match = ch.page;
      else break;
    }
    return match;
  }, [meta, page]);

  // Auto-scroll sidebar so the active chapter stays visible.
  useEffect(() => {
    if (activeBtnRef.current) {
      activeBtnRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [currentChapterPage]);

  return (
    <div className="max-w-6xl mx-auto py-6 px-1" data-testid="reader-page">
      <div className="text-center">
        <div className="text-xs uppercase tracking-[0.25em] text-[#C85A32] font-extrabold inline-flex items-center gap-2">
          <Compass className="w-3.5 h-3.5" /> {c.kicker}
        </div>
        <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold text-[#1C2024] inline-flex items-center gap-2">
          <BookOpen className="w-7 h-7 text-[#C85A32]" /> {c.heading}
        </h1>
        <div className="mt-1 text-sm text-[#4A525A] italic">{c.author_line}</div>
        <p className="mt-3 text-[#4A525A] max-w-2xl mx-auto">{c.sub}</p>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
        <a href={dlHref} data-testid="reader-download"
          className="pod-focus bg-[#C85A32] hover:bg-[#B24C28] text-white font-bold py-2.5 px-5 rounded-xl inline-flex items-center gap-2">
          <Download className="w-4 h-4" /> {c.download}
        </a>
        <a href={pdfHref} target="_blank" rel="noopener noreferrer" data-testid="reader-open-new"
          className="pod-focus border-2 border-[#E4DDD3] hover:border-[#C85A32] text-[#1C2024] font-bold py-2.5 px-5 rounded-xl inline-flex items-center gap-2">
          <ExternalLink className="w-4 h-4" /> {c.open_new}
        </a>
      </div>

      {err ? (
        <div className="mt-6 text-sm text-red-800 bg-red-50 border-2 border-red-200 rounded-xl p-4 text-center" data-testid="reader-error">{err}</div>
      ) : !meta ? (
        <div className="mt-8 text-center text-[#4A525A] flex items-center justify-center gap-2" data-testid="reader-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> {c.loading}
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
          <aside className="bg-white border-2 border-[#E4DDD3] rounded-2xl p-3 lg:sticky lg:top-4 lg:self-start lg:max-h-[80vh] lg:overflow-y-auto" data-testid="reader-chapters">
            <div className="text-xs uppercase tracking-widest font-extrabold text-[#707880] px-2 py-1 inline-flex items-center gap-1">
              <List className="w-3.5 h-3.5" /> {c.chapters}
            </div>
            {(!meta.chapters || meta.chapters.length === 0) ? (
              <p className="mt-1 text-xs text-[#707880] px-2">{c.empty_chapters}</p>
            ) : (
              <ul className="mt-1 space-y-0.5">
                {meta.chapters.map((ch) => {
                  const isActive = ch.page === currentChapterPage;
                  return (
                  <li key={ch.page}>
                    <button
                      ref={isActive ? activeBtnRef : null}
                      onClick={() => jumpTo(ch.page)}
                      data-testid={`reader-chapter-${ch.page}`}
                      aria-current={isActive ? "true" : undefined}
                      className={`pod-focus w-full text-left rounded-lg px-2 py-1.5 text-xs transition-colors ${
                        isActive
                          ? "bg-[#C85A32] text-white font-bold"
                          : "text-[#1C2024] hover:bg-[#FAF5EC]"
                      }`}
                    >
                      <div className="font-semibold leading-snug line-clamp-2">{ch.title}</div>
                      <div className={`mt-0.5 text-[10px] uppercase tracking-widest ${
                        isActive ? "text-white/80" : "text-[#707880]"
                      }`}>{c.jump(ch.page)}</div>
                    </button>
                  </li>
                  );
                })}
              </ul>
            )}
          </aside>

          <div className="rounded-2xl overflow-hidden border-2 border-[#E4DDD3] bg-white shadow-sm" data-testid="reader-frame">
            <iframe
              ref={iframeRef}
              title="Court Ready PDF"
              src={iframeSrc}
              className="w-full"
              style={{ height: "80vh" }}
            />
          </div>
        </div>
      )}

      <p className="mt-6 text-xs text-[#707880] italic text-center max-w-2xl mx-auto" data-testid="reader-disclaimer">{c.disclaimer}</p>
    </div>
  );
}

export default function CourtReadyReader() {
  const { user } = useAuth();
  const tier = user?.tier || "free";
  if (!user || !hasTier(tier, "court_ready_plus")) {
    return <PaywallGate feature="book" minimum="court_ready_plus"><ReaderApp /></PaywallGate>;
  }
  return <ReaderApp />;
}
